import { useEffect, useRef, useState } from 'react';
import { Socket } from 'socket.io-client';
import { useAuth0 } from '@auth0/auth0-react';
import { io } from 'socket.io-client';
import { getApiUrl } from 'src/shared/lib/getApiUrl';
import { useAuth } from 'src/features/auth/contexts/AuthContext';
import { useOrg } from 'src/shared/contexts/OrgContext';

// hack to get an ALB cookie set for sticky sessions
async function setCookieViaIframe() {
  try {
    return await new Promise((resolve, reject) => {
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = getApiUrl() + '/test';

      iframe.onload = () => {
        document.body.removeChild(iframe);
        resolve('');
      };

      iframe.onerror = () => reject('Failed to load iframe in socket context');

      document.body.appendChild(iframe);
    });
  } catch (err) {
    console.error(err);
    return;
  }
}

/**
 * Manages socket connection lifecycle based on org/user changes
 * Returns socket instance and ready state
 */
export function useSocketConnection() {
  const { getAccessTokenSilently } = useAuth0();
  const { currentOrg } = useOrg();
  const { user } = useAuth();

  const [socket, setSocket] = useState<Socket | null>(null);
  const [socketReady, setSocketReady] = useState(false);

  // Ref to track current socket instance - allows cleanup to access socket even if state hasn't updated
  const socketRef = useRef<Socket | null>(null);
  // Track which org the current socket is connected for (prevents reconnecting to wrong org)
  const socketOrgIdRef = useRef<string | null>(null);

  // Socket connection effect - only depends on org/user, not socket itself
  useEffect(() => {
    if (!currentOrg?.id) {
      return;
    }

    const currentOrgId = currentOrg.id;

    // Check if we already have a valid connected socket for this org
    // This prevents unnecessary socket recreation when the effect re-runs due to other dependencies
    const existingSocket = socketRef.current;
    if (
      existingSocket?.connected &&
      typeof existingSocket.auth !== 'function' &&
      existingSocket.auth?.orgId === currentOrgId &&
      socketOrgIdRef.current === currentOrgId
    ) {
      // Valid socket already exists for this org - no need to create a new one
      return;
    }

    // Close existing socket if it's for a different org or not connected
    // This happens when: org switches, socket disconnected, or socket.auth doesn't match
    // We'll handle state updates in cleanup to avoid synchronous setState warnings
    const socketToClose = existingSocket;
    if (socketToClose) {
      // Remove all event listeners before disconnecting to prevent stale callbacks
      socketToClose.off('connect');
      socketToClose.off('disconnect');
      if (socketToClose.connected) {
        socketToClose.disconnect();
      }
      socketRef.current = null;
    }

    // Track which org this socket connection is for
    socketOrgIdRef.current = currentOrgId;

    // Flag to prevent callbacks from running after cleanup (e.g., if org switches during async operations)
    let isCancelled = false;
    let newSocket: Socket | null = null;

    function onConnect() {
      // Ignore connect events if this connection was cancelled (e.g., org switched)
      if (isCancelled) {
        return;
      }
      // Socket successfully connected - mark as ready
      setSocketReady(true);
    }

    function onDisconnect(reason: string) {
      // Ignore disconnect events if this connection was cancelled
      if (isCancelled) {
        return;
      }
      // Socket disconnected - reset ready state and refresh ALB cookie
      setCookieViaIframe();
      setSocketReady(false);
      // If server initiated the disconnect, attempt to reconnect after a delay
      // Only reconnect if we're still for the same org (prevents reconnecting to wrong org)
      if (reason === 'io server disconnect' && newSocket && socketOrgIdRef.current === currentOrgId) {
        setTimeout(() => {
          // Double-check cancellation and org match before reconnecting
          if (!isCancelled && newSocket && socketOrgIdRef.current === currentOrgId) {
            newSocket.connect();
          }
        }, 500);
      }
    }

    (async function openSocket() {
      // Set ALB cookie for sticky sessions before creating socket
      await setCookieViaIframe();

      // Check if cancelled after async operation (e.g., org switched during await)
      if (isCancelled) {
        return;
      }

      // Get auth token for socket authentication
      const token = await getAccessTokenSilently();

      // Check again after another async operation
      if (isCancelled) {
        return;
      }

      // Create new socket instance with org-specific auth and query params
      newSocket = io(getApiUrl() + '/', {
        auth: {
          token: token,
          orgId: currentOrgId,
        },
        closeOnBeforeunload: true,
        withCredentials: true,
        query: {
          email: user?.email,
          org: currentOrg.name,
        },
      });

      // If cancelled after socket creation but before registration, disconnect immediately
      if (isCancelled) {
        newSocket.disconnect();
        return;
      }

      // Store socket in ref and register event handlers
      // The ref allows cleanup to access the socket even if state hasn't updated yet
      socketRef.current = newSocket;
      newSocket.on('connect', onConnect);
      newSocket.on('disconnect', onDisconnect);
      setSocket(newSocket);
    })();

    return () => {
      // Mark as cancelled to prevent callbacks from running after cleanup
      isCancelled = true;

      // Close the socket being created if async hasn't completed yet
      // This handles the case where org switches before socket creation finishes
      // Only close if it's not already in the ref (to avoid double-closing)
      if (newSocket && socketRef.current !== newSocket) {
        newSocket.off('connect', onConnect);
        newSocket.off('disconnect', onDisconnect);
        if (newSocket.connected) {
          newSocket.disconnect();
        }
      }

      // Close existing socket from ref (this is the primary socket to close)
      // The ref ensures we can always access the current socket instance, even if
      // state hasn't updated yet or the effect re-runs quickly
      const socketToCloseFromRef = socketRef.current;
      if (socketToCloseFromRef) {
        // Remove all listeners before disconnecting
        socketToCloseFromRef.off('connect');
        socketToCloseFromRef.off('disconnect');
        if (socketToCloseFromRef.connected) {
          socketToCloseFromRef.disconnect();
        }
        socketRef.current = null;
      }

      // Also close the socket we closed at the start of this effect (safety net)
      // This handles edge cases where a socket might exist outside the ref
      // Only close if it's different from the ref socket to avoid double-closing
      if (socketToClose && socketToClose !== socketToCloseFromRef) {
        socketToClose.off('connect');
        socketToClose.off('disconnect');
        if (socketToClose.connected) {
          socketToClose.disconnect();
        }
      }

      // Update state to reflect that socket is closed
      setSocket(null);
      setSocketReady(false);

      // Only clear orgId ref if we're actually switching away from this org
      // This prevents clearing the ref if cleanup runs for other reasons (e.g., component unmount)
      if (socketOrgIdRef.current === currentOrgId) {
        socketOrgIdRef.current = null;
      }
    };
  }, [currentOrg, user?.email, getAccessTokenSilently]);

  return { socket, socketReady };
}
